import React, { useEffect, useMemo, useState } from 'react';

import { ViewType } from '@eika/hooks';
import { IconTypes } from '@eika/icon';

import { ActionButtonGroup } from '../../components/shared/ActionButtonGroup';
import CreateRecurringPayment from '../../components/transaction/RecurringPayment/CreateRecurringPayment';
import CreateRecurringTransfer from '../../components/transaction/RecurringTransfer/CreateRecurringTransfer';
import { useRefreshRecurringPayments } from '../../fetchHooks/useRecurringPayments';
import type { AccountNumber } from '../../generated-types';
import useQueryParam from '../../hooks/queryparams/useQueryParam';
import useHasView from '../../hooks/useHasView';
import useTranslation from '../../hooks/useTranslation';

import './RecurringPaymentsActions.scss';

interface Props {
    views?: ViewType[];
}

const RecurringPaymentsActions: React.FC<Props> = ({ views = [ViewType.desktop] }) => {
    const isVisible = useHasView(views);
    const translated = useTranslation();
    const searchParamBsuAccountNumber = useQueryParam('bsuaccountnumber');

    const [showNewRecurringPaymentModal, setNewRecurringPaymentModal] = useState(false);
    const [showRecurringTransferModal, setShowRecurringTransferModal] = useState(false);
    const refresh = useRefreshRecurringPayments();

    const bsuAccountNumber = useMemo(() => {
        return searchParamBsuAccountNumber ? ({ value: searchParamBsuAccountNumber } as AccountNumber) : undefined;
    }, [searchParamBsuAccountNumber]);

    useEffect(() => {
        if (bsuAccountNumber) setShowRecurringTransferModal(!!bsuAccountNumber);
    }, [bsuAccountNumber]);

    if (!isVisible) return <></>;

    const closeRecurringTransferModal = (openPayment: boolean = false) => {
        setShowRecurringTransferModal(false);
        if (openPayment) setNewRecurringPaymentModal(true);
    };

    return (
        <div id="recurring-payment-action-buttons" className="recurring-payments-actions">
            <ActionButtonGroup
                actions={[
                    {
                        icon: IconTypes.FAST_BETALING_36,
                        className: 'new-recurring-payment__button',
                        onClick: () => setNewRecurringPaymentModal(true),
                        children: translated.recurringPayments.recurringPayment,
                    },
                    {
                        icon: IconTypes.FAST_OVERFOR_36,
                        className: 'new-recurring-payment__button',
                        onClick: () => setShowRecurringTransferModal(true),
                        children: translated.recurringPayments.recurringTransfer,
                    },
                ]}
            />
            {showNewRecurringPaymentModal && (
                <CreateRecurringPayment
                    show={showNewRecurringPaymentModal}
                    setShow={setNewRecurringPaymentModal}
                    refresh={refresh}
                />
            )}
            {showRecurringTransferModal && (
                <CreateRecurringTransfer
                    show={showRecurringTransferModal}
                    setShow={setShowRecurringTransferModal}
                    onCloseModal={closeRecurringTransferModal}
                    refresh={refresh}
                    bsuAccountNumber={bsuAccountNumber}
                />
            )}
        </div>
    );
};

export default RecurringPaymentsActions;
